import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarCheck, CircleCheck } from "lucide-react";
import VideoCard from "./VideoCard";

export default function ServiceModal({ detail, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {detail && (
        <motion.div
          data-testid="service-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[90] flex items-end justify-center bg-navy-950/70 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            data-testid="service-modal"
            role="dialog"
            aria-modal="true"
            aria-label={detail.title}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-slate-100 bg-white/95 px-6 py-4 backdrop-blur-sm sm:px-8">
              <h3 className="font-heading text-lg font-bold tracking-tight text-navy sm:text-xl">{detail.title}</h3>
              <button
                data-testid="service-modal-close-btn"
                onClick={onClose}
                aria-label="Close"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-5 px-6 py-6 sm:px-8">
              {detail.paragraphs.map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-slate-600 sm:text-base">{p}</p>
              ))}

              {detail.bullets && (
                <div className="rounded-2xl bg-navy-50/60 p-5">
                  <p className="font-heading text-sm font-bold text-navy">{detail.bulletsTitle}</p>
                  <ul className="mt-3 space-y-2">
                    {detail.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {detail.images && (
                <div className={`grid gap-4 ${detail.images.length > 1 ? "sm:grid-cols-2" : ""}`}>
                  {detail.images.map((img) => (
                    <figure key={img.src} className="text-center">
                      {img.scope ? (
                        <div className="scope-frame mx-auto aspect-square w-full max-w-[240px]">
                          <img src={img.src} alt={img.label} loading="lazy" className="h-full w-full object-cover" />
                        </div>
                      ) : (
                        <img src={img.src} alt={img.label} loading="lazy" className="aspect-[4/3] w-full rounded-2xl object-cover ring-1 ring-slate-200" />
                      )}
                      <figcaption className="mt-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{img.label}</figcaption>
                    </figure>
                  ))}
                </div>
              )}

              {detail.video && (
                <VideoCard
                  label={detail.video.label}
                  desc={detail.video.desc}
                  videoFile={detail.video.videoFile}
                  videoFileWebm={detail.video.videoFileWebm}
                  poster={detail.video.poster}
                  testId={`service-modal-video-${detail.video.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                />
              )}

              {detail.footnote && (
                <p className="rounded-xl bg-brand/5 px-4 py-3 text-sm leading-relaxed text-slate-600 ring-1 ring-brand/15">{detail.footnote}</p>
              )}
            </div>

            <div className="border-t border-slate-100 px-6 py-5 sm:px-8">
              <Link
                to="/book-appointment"
                data-testid="service-modal-book-btn"
                onClick={onClose}
                className="flex w-full items-center justify-center gap-2.5 rounded-full bg-brand px-6 py-3.5 font-heading text-sm font-bold text-white shadow-lg shadow-teal-900/20 transition-colors hover:bg-brand-light"
              >
                <CalendarCheck className="h-4 w-4" /> Book Consultation
              </Link>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
